import { SignInIdentifier, type ConsentInfoResponse } from '@logto/schemas';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import type { ReactNode } from 'react';
import { useContext } from 'react';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import BrandingHeader from '@/components/BrandingHeader';
import { getBrandingLogoUrl } from '@/shared/utils/logo';
import { layoutClassNames } from '@/utils/consts';

import FirstScreenLayout from '../FirstScreenLayout';

import styles from './index.module.scss';

type ThirdPartyBranding = ConsentInfoResponse['application']['branding'];

type Props = {
  readonly children: ReactNode;
  readonly title: TFuncKey;
  readonly titleInterpolation?: Record<string, unknown>;
  readonly thirdPartyBranding?: ThirdPartyBranding;
};

const LandingPageLayout = ({ children, title, titleInterpolation, thirdPartyBranding }: Props) => {
  const { experienceSettings, theme } = useContext(PageContext);

  if (!experienceSettings) {
    return null;
  }

  const {
    color: { isDarkModeEnabled },
    branding,
  } = experienceSettings;
  const isMedraSignIn = title === 'description.sign_in_to_your_account';
  const isMedraRegister = title === 'description.create_your_account';
  const isAdminConsole = experienceSettings.appId === 'admin-console';
  const isMedraEntry = !isAdminConsole && (isMedraSignIn || isMedraRegister);
  const signUpIdentifiers = experienceSettings.signUp.identifiers;
  const signInIdentifiers = experienceSettings.signIn.methods.map(({ identifier }) => identifier);
  const isEmailOnlyRegister =
    signUpIdentifiers.length === 1 && signUpIdentifiers[0] === SignInIdentifier.Email;
  const isUsernameOnlyRegister =
    signUpIdentifiers.length === 1 && signUpIdentifiers[0] === SignInIdentifier.Username;
  const isEmailOnlySignIn =
    signInIdentifiers.length === 1 && signInIdentifiers[0] === SignInIdentifier.Email;
  const isUsernameOnlySignIn =
    signInIdentifiers.length === 1 && signInIdentifiers[0] === SignInIdentifier.Username;
  const registerDescription = isEmailOnlyRegister
    ? '填写邮箱并完成验证，设置密码后即可登录。'
    : isUsernameOnlyRegister
      ? '设置用户名和密码，完成后即可登录。'
      : '创建统一身份账户，完成后即可登录。';
  const signInDescription = isEmailOnlySignIn
    ? '使用注册邮箱和密码登录。'
    : isUsernameOnlySignIn
      ? '使用用户名和密码登录。'
      : '使用已注册的账户凭据登录。';

  return (
    <FirstScreenLayout pageMeta={{ titleKey: title, titleKeyInterpolation: titleInterpolation }}>
      {isMedraEntry ? (
        <header className={styles.medraHeader}>
          <h2>{isMedraRegister ? '注册账号' : '登录 MedRA'}</h2>
          <p>{isMedraRegister ? registerDescription : signInDescription}</p>
        </header>
      ) : (
        <BrandingHeader
          className={classNames(styles.header, layoutClassNames.brandingHeader)}
          headline={title}
          headlineInterpolation={titleInterpolation}
          logo={getBrandingLogoUrl({ theme, branding, isDarkModeEnabled })}
          thirdPartyLogo={
            thirdPartyBranding &&
            getBrandingLogoUrl({ theme, branding: thirdPartyBranding, isDarkModeEnabled })
          }
        />
      )}
      {children}
    </FirstScreenLayout>
  );
};

export default LandingPageLayout;
